import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from '@chakra-ui/core';
import { LinkIcon } from '@chakra-ui/icons';
import copy from 'copy-to-clipboard';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import config from '../config/config';
import {
  ELECTION_BETTING_END_DATE,
  ELECTION_RESOLUTION_DATE,
} from '../utils/Election';
import { toBTC } from '../utils/Utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './Table/Table';

interface BetsTableState {
  tabIndex: number;
  selectedBets: string[];
}

type BetsTableContextType = [BetsTableState, (BetsTableState) => void];

export const BetsTableContext = React.createContext<BetsTableContextType>([
  { tabIndex: 0, selectedBets: [] },
  () => {},
]);

const ACTIVE_STAGES = ['WITHDRAWABLE', 'ACTIVE'];
const OFFER_STAGES = ['OFFER', 'ACCEPT', 'SIGN'];
const CLOSED_STAGES = ['CLOSED', 'CANCELLED'];

export const BetsTableProvider = ({ children }) => {
  const [state, setState] = useState({ tabIndex: 0, selectedBets: [] });
  return (
    <BetsTableContext.Provider value={[state, setState]}>
      {children}
    </BetsTableContext.Provider>
  );
};

const BetsTable = ({ bets, showCreateOffer }) => {
  const [state, setState] = useContext(BetsTableContext);
  const { tabIndex, selectedBets } = state;

  const setTabIndex = (tabIndex: number) => {
    setState({ ...state, tabIndex });
  };

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  const activeBets = bets.filter((bet) => ACTIVE_STAGES.includes(bet.stage));
  const offeredBets = bets.filter((bet) => OFFER_STAGES.includes(bet.stage));
  const closedBets = bets.filter((bet) => CLOSED_STAGES.includes(bet.stage));

  return (
    <Box
      bg="white"
      h="fit-content"
      w="100%"
      borderRadius="4px"
      gridArea="bets"
      p={4}
    >
      <Tabs
        size="sm"
        isLazy
        variant="unstyled"
        index={tabIndex}
        onChange={handleTabsChange}
      >
        <TabList mb={4}>
          <Tab
            border="solid 1px #32325d"
            borderRadius="4px 0 0 4px"
            _selected={{ bg: '#32325d', color: 'white' }}
          >
            Active Bets ({activeBets.length})
          </Tab>
          <Tab
            borderTop="solid 1px #32325d"
            borderBottom="solid 1px #32325d"
            _selected={{ bg: '#32325d', color: 'white' }}
          >
            Active Offers ({offeredBets.length})
          </Tab>
          <Tab
            borderRadius="0 4px 4px 0"
            border="solid 1px #32325d"
            _selected={{ bg: '#32325d', color: 'white' }}
          >
            Past Bets ({closedBets.length})
          </Tab>

          {showCreateOffer && (
            <Box ml="auto">
              <RouterLink to="/new">
                <Button size="sm" colorScheme="blue">
                  New Bet Offer
                </Button>
              </RouterLink>
            </Box>
          )}
        </TabList>

        <TabPanels>
          <TabPanel padding={0}>
            <Box>
              <ActiveBets bets={activeBets} selectedBets={selectedBets} />
            </Box>
          </TabPanel>
          <TabPanel padding={0}>
            <Box>
              <OfferedBets bets={offeredBets} selectedBets={selectedBets} />
            </Box>
          </TabPanel>
          <TabPanel padding={0}>
            <Box>
              <ClosedBets bets={closedBets} selectedBets={selectedBets} />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const TableEmptyState = ({ label }) => (
  <Flex alignItems="center" justifyContent="center" bg="#F6F6F6" h="200px">
    <Heading fontWeight={500} size="md" color="#7c7c7c">
      {label}
    </Heading>
  </Flex>
);

const ActiveBets = ({ bets, selectedBets }) =>
  bets.length === 0 ? (
    <TableEmptyState label="No Active Bets" />
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Link</TableHeader>
          <TableHeader>Bet Type</TableHeader>
          <TableHeader>Prediction</TableHeader>
          <TableHeader>Bet Amount</TableHeader>
          <TableHeader>Bet Odds</TableHeader>
          <TableHeader>Total Payout</TableHeader>
          <TableHeader>Bet Ends</TableHeader>
          <TableHeader>Tx Details</TableHeader>

          <TableHeader />
        </TableRow>
      </TableHead>
      <TableBody>
        {bets.map((bet) => (
          <OfferedBetRow
            bet={bet}
            selected={selectedBets.includes(bet.contractId)}
          />
        ))}
      </TableBody>
    </Table>
  );

const OfferedBets = ({ bets, selectedBets }) =>
  bets.length === 0 ? (
    <TableEmptyState label="No Active Offers" />
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Link</TableHeader>
          <TableHeader>Bet Type</TableHeader>
          <TableHeader>Prediction</TableHeader>
          <TableHeader>Bet Amount</TableHeader>
          <TableHeader>Bet Odds</TableHeader>
          <TableHeader>Total Payout</TableHeader>
          <TableHeader>Offer Expires</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader />
        </TableRow>
      </TableHead>
      <TableBody>
        {bets.map((bet) => (
          <OfferedBetRow
            bet={bet}
            selected={selectedBets.includes(bet.contractId)}
          />
        ))}
      </TableBody>
    </Table>
  );

const OfferedBetRow = ({ bet, selected }) => {
  const {
    betDetails: { prediction, resolutionDate, betAmount, betAmountCounter },
  } = bet;

  const { isLocalParty, localPartyInputs, remotePartyInputs } = bet.contract;

  if (isLocalParty) {
    console.log('localPartyInputs utxos', localPartyInputs.utxos);
  } else {
    console.log('remotePartyInputs utxos', remotePartyInputs.utxos);
  }

  const history = useHistory();
  const [copiedTooltipOpen, setCopiedTooltipOpen] = useState(false);
  const viewBetLink = `https://atomic.finance/odds/app/bet/${bet.contractId}`;
  const viewOfferLink = `https://atomic.finance/odds/app/offer/${bet.contractId}`;

  const hasAction = () => {
    switch (bet.stage) {
      case 'OFFER':
        if (new Date() > ELECTION_BETTING_END_DATE) return false;
        return bet.towerInfo?.accept;
      case 'ACCEPT':
        if (new Date() > ELECTION_BETTING_END_DATE) return false;
        return bet.towerInfo?.sign;
      case 'WITHDRAWABLE':
        return true;
      default:
        return false;
    }
  };

  const canCancel = () => {
    switch (bet.stage) {
      case 'OFFER':
        return true;
      case 'ACCEPT':
        return true;
      case 'SIGN':
        return true;
      default:
        return false;
    }
  };

  const getStatus = () => {
    switch (bet.stage) {
      case 'OFFER':
        return bet.towerInfo?.accept
          ? 'Offer Accepted'
          : 'Pending Counterparty';
      case 'ACCEPT':
        return bet.towerInfo?.sign
          ? 'Ready to Broadcast'
          : 'Pending Confirmation';
      case 'SIGN':
        return 'Waiting for finalization';
      case 'WITHDRAWABLE':
        return 'You Won!';
      case 'ACTIVE':
        return (
          <Link
            textDecoration="underline"
            color="blue.600"
            href={`${config.bitcoin.explorer}/${bet.contract.fundTxId}`}
            target="_blank"
          >
            {bet.contract.fundTxId.substring(0, 3)}...
            {bet.contract.fundTxId.slice(-3)}
          </Link>
        );
      case 'CLOSED':
        return 'Closed';
      case 'CANCELLED':
        return 'Cancelled';
    }
  };

  const copyOfferLink = () => {
    copy(bet.stage === 'OFFER' ? viewOfferLink : viewBetLink);
    setCopiedTooltipOpen(true);
    setTimeout(() => setCopiedTooltipOpen(false), 2000);
  };

  return (
    <TableRow bg={selected ? '#E9EAFB' : 'white'}>
      <TableCell>
        <Tooltip isOpen={copiedTooltipOpen} label="Copied to clipboard">
          <IconButton
            onClick={copyOfferLink}
            aria-label="Copy Link"
            variant="ghost"
            icon={<LinkIcon color="#6772e5" />}
          />
        </Tooltip>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">Presidential Election</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm" textTransform="capitalize">
          {prediction}
        </Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{toBTC(betAmount)} BTC</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">
          1 : {parseFloat((betAmountCounter / betAmount).toFixed(1))}
        </Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{toBTC(betAmount + betAmountCounter)} BTC</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">
          {moment(
            new Date(resolutionDate).toISOString() ===
              ELECTION_RESOLUTION_DATE.toISOString()
              ? new Date('2020-11-04T02:00:00.000Z')
              : resolutionDate
          ).fromNow()}
        </Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{getStatus()}</Text>
      </TableCell>
      {bet.stage === 'CANCELLED' && bet.withdrawable && (
        <TableCell>
          <Text fontSize="sm">Cancelled</Text>
          <Text
            color="#6772e5"
            cursor="pointer"
            fontSize="sm"
            fontWeight={500}
            display="inline"
            marginRight="5px"
            marginLeft="5px"
          >
            |
          </Text>
          <Text
            color="#6772e5"
            cursor="pointer"
            fontSize="sm"
            fontWeight={500}
            display="inline"
            onClick={() => history.push(`/bet/${bet.contractId}`)}
          >
            Withdraw
          </Text>
        </TableCell>
      )}
      <TableCell textAlign="center">
        {hasAction() && (
          <Text
            color="#6772e5"
            cursor="pointer"
            fontSize="sm"
            fontWeight={500}
            display="inline"
            onClick={() => history.push(`/bet/${bet.contractId}`)}
          >
            {bet.stage === 'WITHDRAWABLE' ? 'Withdraw Winnings' : 'Confirm'}
          </Text>
        )}
        {hasAction() && canCancel() && (
          <Text
            color="#6772e5"
            cursor="pointer"
            fontSize="sm"
            fontWeight={500}
            display="inline"
            marginRight="5px"
            marginLeft="5px"
          >
            |
          </Text>
        )}
        {canCancel() && (
          <Text
            color="#6772e5"
            cursor="pointer"
            fontSize="sm"
            fontWeight={500}
            display="inline"
            onClick={() => history.push(`/bet/${bet.contractId}`)}
          >
            Cancel
          </Text>
        )}
      </TableCell>
    </TableRow>
  );
};

const ClosedBets = ({ bets, selectedBets }) =>
  bets.length === 0 ? (
    <TableEmptyState label="No Past Bets" />
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Link</TableHeader>

          <TableHeader>Bet Type</TableHeader>
          <TableHeader>Prediction</TableHeader>
          <TableHeader>Bet Amount</TableHeader>
          <TableHeader>Bet Odds</TableHeader>
          <TableHeader>Total Payout</TableHeader>
          <TableHeader>Offer Expires</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader />
        </TableRow>
      </TableHead>
      <TableBody>
        {bets.map((bet) => (
          <OfferedBetRow
            bet={bet}
            selected={selectedBets.includes(bet.contractId)}
          />
        ))}
      </TableBody>
    </Table>
  );

export default BetsTable;
